import { useMemo } from 'react'
import { getUsers, useGetUsersInfinite } from '@/api/generated/user/user'
import type {
  GetUsersParams,
  UserListAdminApiOutContract,
  UserListItemAdminApiOutContract,
} from '@/api/generated/pwrdbyQuickMDAdminsApi.schemas'
import { errorCodes, getUserFriendlyErrorMessage } from '@/utils/errormessages'

const PAGE_SIZE = 20

/**
 * Reads the paginated clinic-members list. Replaces the `getClinicMembers` thunk +
 * GET_CLINIC_MEMBERS* reducer (which appended pages, deduped by `${UserId}-${UserType}`
 * and tracked loading/error/lastListIncrease).
 *
 * Filters/search live in the URL search params; this hook derives a typed
 * GetUsersParams from them, so changing any filter or the search value produces a new
 * query key and React Query resets the list automatically (no manual clear needed).
 */
const toBool = (value: string | null): boolean | undefined => {
  if (value === null) return undefined
  return value === 'true'
}

export const buildClinicMembersParams = (searchParams: URLSearchParams): GetUsersParams => {
  const params: GetUsersParams = { Count: PAGE_SIZE, Offset: 0 }

  const searchValue = searchParams.get('searchValue')
  if (searchValue) params.SearchValue = searchValue

  const stateCodes = searchParams.get('StateCodes')
  if (stateCodes) params.StateCodes = stateCodes

  const visitTypeIds = searchParams.get('VisitTypeIds')
  if (visitTypeIds) params.VisitTypeIds = visitTypeIds

  const sortOrder = searchParams.get('SortOrder')
  if (sortOrder) params.SortOrder = sortOrder

  const isDoctor = toBool(searchParams.get('IsDoctor'))
  if (isDoctor !== undefined) params.IsDoctor = isDoctor

  const isAdmin = toBool(searchParams.get('IsAdmin'))
  if (isAdmin !== undefined) params.IsAdmin = isAdmin

  const isActive = toBool(searchParams.get('IsActive'))
  if (isActive !== undefined) params.IsActive = isActive

  const isVerified = toBool(searchParams.get('IsVerified'))
  if (isVerified !== undefined) params.IsVerified = isVerified

  return params
}

export const useClinicMembers = (searchParams: URLSearchParams, enabled = true) => {
  const baseParams = useMemo(() => buildClinicMembersParams(searchParams), [searchParams])

  const query = useGetUsersInfinite(baseParams, {
    query: {
      enabled,
      initialPageParam: 0,
      // Keep results fresh for 60s so returning to the Management tab (remount) serves the cached
      // list instead of refetching every time. gcTime default (5m) keeps the cache across tab switches.
      staleTime: 60_000,
      // The generated infinite queryFn ignores pageParam, so override it to fetch each
      // page at the right Offset while keeping the rest of the params from the URL.
      queryFn: ({ pageParam, signal }) => {
        const offset = typeof pageParam === 'number' ? pageParam : 0
        return getUsers({ ...baseParams, Offset: offset }, undefined, signal)
      },
      getNextPageParam: (lastPage: UserListAdminApiOutContract, allPages: UserListAdminApiOutContract[]) => {
        const lastPageUsers = lastPage.Users ?? []
        const totalLoaded = allPages.reduce((sum, page) => sum + (page.Users?.length ?? 0), 0)
        const totalCount = lastPage.TotalCount ?? 0
        if (totalLoaded >= totalCount && lastPageUsers.length < PAGE_SIZE) return undefined
        if (lastPageUsers.length < PAGE_SIZE) return undefined
        return allPages.length * PAGE_SIZE
      },
    },
  })

  const members = useMemo<UserListItemAdminApiOutContract[]>(() => {
    const pages = query.data?.pages ?? []
    const seen = new Set<string>()
    const flat: UserListItemAdminApiOutContract[] = []
    pages.forEach((page) => {
      ;(page.Users ?? []).forEach((member) => {
        const key = `${member.UserId}-${member.UserType}`
        if (!seen.has(key)) {
          seen.add(key)
          flat.push(member)
        }
      })
    })
    return flat
  }, [query.data])

  const totalCount = useMemo(() => {
    const pages = query.data?.pages ?? []
    const lastPage = pages[pages.length - 1]
    return lastPage?.TotalCount ?? 0
  }, [query.data])

  const errorMessage = useMemo(
    () => (query.isError ? getUserFriendlyErrorMessage(errorCodes.DEFAULT) : ''),
    [query.isError],
  )

  return {
    ...query,
    members,
    totalCount,
    errorMessage,
  }
}
